import React, { useState } from 'react';
import { Check, X, Star, Zap } from 'lucide-react';

const PLANS = [
  {
    id: 'free',
    name: 'FREE',
    price: 0,
    period: 'month',
    color: '#6b7280',
    bg: '#f9fafb',
    border: '#e5e7eb',
    badge: null,
    features: [
      { text: '3 Active Requirements', included: true },
      { text: '10 Candidates in Talent Pool', included: true },
      { text: '2 Candidate Deployments per Requirement', included: true },
      { text: 'Basic Candidate Tracking', included: true },
      { text: 'AI Features', included: false },
      { text: 'Automation', included: false },
      { text: 'Analytics', included: false },
    ],
    cta: 'Current Plan',
    ctaBg: '#f3f4f6',
    ctaColor: '#6b7280',
    disabled: true,
  },
  {
    id: 'pro',
    name: 'PRO',
    price: 4999,
    period: 'month',
    color: '#6366f1',
    bg: '#f5f3ff',
    border: '#c4b5fd',
    badge: 'Popular',
    features: [
      { text: '15 Active Requirements', included: true },
      { text: '75 Candidates in Talent Pool', included: true },
      { text: '7 Candidate Deployments per Requirement', included: true },
      { text: 'Direct Deployment from Talent Pool', included: true },
      { text: 'Candidate Lifecycle Tracking', included: true },
      { text: 'Interview Scheduling', included: true },
      { text: 'Basic Analytics', included: true },
      { text: 'AI Resume Generation (Limited)', included: true },
      { text: 'Automation', included: false },
      { text: 'Bulk Actions', included: false },
      { text: 'Advanced AI', included: false },
    ],
    cta: 'Upgrade to Pro',
    ctaBg: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
    ctaColor: 'white',
    disabled: false,
  },
  {
    id: 'enterprise',
    name: 'ENTERPRISE',
    price: 14999,
    period: 'month',
    color: '#f59e0b',
    bg: '#fffbeb',
    border: '#fde68a',
    badge: 'Recommended ⭐',
    features: [
      { text: 'Unlimited Active Requirements', included: true },
      { text: 'Unlimited Talent Pool', included: true },
      { text: 'Unlimited Candidate Deployments', included: true },
      { text: 'AI Video Interviews & Candidate Scoring', included: true },
      { text: 'Bulk Upload & Bulk Actions', included: true },
      { text: 'Workflow Automation (Follow-ups, Notifications)', included: true },
      { text: 'Real-Time Pipeline Visibility', included: true },
      { text: 'Multi-User Access (Teams)', included: true },
      { text: 'Advanced Analytics & Reporting', included: true },
      { text: 'API Access & Integrations', included: true },
      { text: 'Priority Support', included: true },
    ],
    cta: 'Contact Sales',
    ctaBg: 'linear-gradient(135deg,#f59e0b,#d97706)',
    ctaColor: 'white',
    disabled: false,
  },
];

export default function BillingPage({ user }) {
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleUpgrade = (plan) => {
    if (plan.disabled) return;
    setSelected(plan);
    setShowModal(true);
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", sans-serif', maxWidth: '1100px' }}>
      
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ color: '#0f172a', fontSize: '28px', fontWeight: '800', margin: '0 0 8px', letterSpacing: '-0.5px' }}>
          Simple, Transparent Pricing
        </h1>
        <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>
          Choose the plan that works best for your hiring needs
        </p>
      </div>

      {/* Plans Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '40px' }}>
        {PLANS.map(plan => (
          <div key={plan.id} style={{ background: 'white', borderRadius: '16px', border: `2px solid ${plan.border}`, overflow: 'hidden', position: 'relative', transition: 'all 0.2s', boxShadow: plan.id === 'enterprise' ? '0 8px 32px rgba(245,158,11,0.15)' : '0 1px 4px rgba(0,0,0,0.06)' }}>
            
            {/* Badge */}
            {plan.badge && (
              <div style={{ background: plan.id === 'enterprise' ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', textAlign: 'center', padding: '6px', fontSize: '11px', fontWeight: '700' }}>
                {plan.badge}
              </div>
            )}

            <div style={{ padding: '24px' }}>
              {/* Plan Name */}
              <div style={{ color: plan.color, fontSize: '13px', fontWeight: '800', letterSpacing: '1px', marginBottom: '8px' }}>{plan.name}</div>
              
              {/* Price */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '20px' }}>
                <span style={{ color: '#0f172a', fontSize: '36px', fontWeight: '800', letterSpacing: '-1px' }}>
                  {plan.price === 0 ? 'Free' : '₹' + plan.price.toLocaleString()}
                </span>
                {plan.price > 0 && <span style={{ color: '#9ca3af', fontSize: '13px' }}>/ month</span>}
              </div>

              {/* CTA Button */}
              <button onClick={() => handleUpgrade(plan)} disabled={plan.disabled}
                style={{ width: '100%', padding: '11px', background: plan.ctaBg, color: plan.ctaColor, border: plan.disabled ? '1px solid #e5e7eb' : 'none', borderRadius: '8px', cursor: plan.disabled ? 'default' : 'pointer', fontSize: '13px', fontWeight: '700', fontFamily: 'inherit', marginBottom: '20px', transition: 'all 0.15s' }}>
                {plan.cta}
              </button>

              {/* Features */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: f.included ? '#dcfce7' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {f.included 
                        ? <Check size={11} color="#16a34a" strokeWidth={3}/>
                        : <X size={11} color="#dc2626" strokeWidth={3}/>
                      }
                    </div>
                    <span style={{ color: f.included ? '#374151' : '#9ca3af', fontSize: '13px', textDecoration: f.included ? 'none' : 'line-through' }}>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px' }}>
        <h3 style={{ color: '#0f172a', fontSize: '15px', fontWeight: '700', margin: '0 0 16px' }}>Frequently Asked Questions</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {[
            { q: 'Can I upgrade anytime?', a: 'Yes! You can upgrade or downgrade your plan at any time.' },
            { q: 'Is there a free trial?', a: 'Our Free plan lets you try Skillora with no credit card required.' },
            { q: 'What payment methods do you accept?', a: 'We accept UPI, credit/debit cards, and net banking.' },
            { q: 'Can I cancel anytime?', a: 'Yes, you can cancel your subscription at any time.' },
          ].map((faq, i) => (
            <div key={i} style={{ background: '#f9fafb', borderRadius: '8px', padding: '14px' }}>
              <div style={{ color: '#0f172a', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>{faq.q}</div>
              <div style={{ color: '#6b7280', fontSize: '12px', lineHeight: '1.5' }}>{faq.a}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade Modal */}
      {showModal && selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setShowModal(false)}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '40px', maxWidth: '440px', width: '100%', boxShadow: '0 40px 80px rgba(0,0,0,0.2)', textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            
            <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: selected.id === 'enterprise' ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              {selected.id === 'enterprise' ? <Star size={28} color="white"/> : <Zap size={28} color="white"/>}
            </div>

            <h2 style={{ color: '#0f172a', fontSize: '22px', fontWeight: '800', margin: '0 0 8px' }}>
              {selected.id === 'enterprise' ? 'Contact Sales' : `Upgrade to ${selected.name}`}
            </h2>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 24px' }}>
              {selected.id === 'enterprise' 
                ? 'Our sales team will reach out to you within 24 hours to set up your Enterprise plan.'
                : `You'll be charged ₹${selected.price.toLocaleString()}/month. Cancel anytime.`}
            </p>

            {selected.id === 'enterprise' ? (
              <div>
                <a href="mailto:sales@skillora.in" style={{ display: 'block', width: '100%', padding: '13px', background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', textDecoration: 'none', marginBottom: '10px', boxSizing: 'border-box' }}>
                  📧 Email Sales Team
                </a>
                <a href="tel:+919999999999" style={{ display: 'block', width: '100%', padding: '13px', background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', textDecoration: 'none', boxSizing: 'border-box' }}>
                  📞 Call Us
                </a>
              </div>
            ) : (
              <div>
                <button style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: 'inherit', marginBottom: '10px' }}
                  onClick={() => { alert('Payment gateway integration coming soon!'); setShowModal(false); }}>
                  💳 Pay ₹{selected.price.toLocaleString()}/month
                </button>
                <button onClick={() => setShowModal(false)} style={{ width: '100%', padding: '13px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit' }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
